import Image from 'next/image'

import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'
import { useSettings } from '@/components/providers/settings-provider'

const OPTIMIZABLE = /^https:\/\/[a-z0-9-]+\.cdn\.modulify\.website\//i

interface BrandMarkProps {
    size?: number
    className?: string
    priority?: boolean
}

// The official logo exactly as uploaded: never redrawn, recoloured, or cropped. The round frame only
// trims the plain background corners outside the scalloped badge.
export const BrandMark: FC<BrandMarkProps> = ({ size = 56, className, priority = false }): ReactNode => {
    const settings = useSettings()

    return (
        <Image
            src={settings.logo}
            alt={settings.logoAlt}
            width={size}
            height={size}
            priority={priority}
            quality={90}
            unoptimized={!OPTIMIZABLE.test(settings.logo)}
            className={cn('shrink-0 rounded-full object-contain', className)}
            style={{ width: size, height: size }}
        />
    )
}

interface LogoProps {
    className?: string
    tone?: 'dark' | 'light'
    size?: number
    priority?: boolean
}

export const Logo: FC<LogoProps> = ({ className, tone = 'dark', size = 52, priority = false }): ReactNode => {
    const settings = useSettings()
    const dark = tone === 'dark'

    return (
        <span className={cn('inline-flex items-center gap-3', className)}>
            <BrandMark size={size} priority={priority} className={cn('shadow-[0_6px_18px_-10px_rgba(58,40,30,0.55)]', dark ? 'ring-2 ring-white/80' : 'ring-2 ring-white/20')} />
            <span className='flex flex-col leading-none'>
                <span className={cn('font-heading text-[1.3rem] font-semibold tracking-[0.04em] font-soft sm:text-[1.4rem]', dark ? 'text-cocoa-900' : 'text-cream-50')}>
                    {settings.id
                        ? <span data-mdlfy-cms={`store_settings:${settings.id}:store_name`}>{settings.storeName}</span>
                        : settings.storeName}
                </span>
                {settings.tagline && (
                    <span className={cn('mt-1.5 text-[10px] font-bold uppercase tracking-[0.2em]', dark ? 'text-blush-600' : 'text-blush-200')}>
                        {settings.id
                            ? <span data-mdlfy-cms={`store_settings:${settings.id}:tagline`}>{settings.tagline}</span>
                            : settings.tagline}
                    </span>
                )}
            </span>
        </span>
    )
}
